import React from 'react';
import type { components } from '../../../../types/learnerApi';

type Config = components['schemas']['SanitizedFreeTextEssayConfig'];

export interface FreeTextEssayAnswer {
  text: string;
}

export interface FreeTextEssayViewProps {
  item: { id: string; points?: number; config: Config };
  mode: 'learner' | 'preview';
  value?: FreeTextEssayAnswer;
  onChange?: (value: FreeTextEssayAnswer) => void;
  disabled?: boolean;
}

const FreeTextEssayView: React.FC<FreeTextEssayViewProps> = ({
  item,
  mode,
  value,
  onChange,
  disabled,
}) => {
  const text = value?.text ?? '';
  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
  const { min_words, max_words } = item.config;

  const overLimit = max_words != null && wordCount > max_words;
  const underMin = min_words != null && wordCount < min_words && text.length > 0;

  return (
    <div className="space-y-3">
      <p className="font-medium text-text-primary">{item.config.prompt}</p>
      <textarea
        value={text}
        onChange={(e) => {
          if (disabled || mode === 'preview') return;
          onChange?.({ text: e.target.value });
        }}
        disabled={disabled}
        rows={6}
        placeholder="Write your response…"
        className={`w-full px-3 py-2 rounded-lg border bg-white dark:bg-white/[0.04] text-text-primary placeholder:text-text-tertiary text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60
          ${overLimit ? 'border-error-500' : 'border-border-primary dark:border-white/15'}
        `}
      />
      <div className="flex items-center justify-between text-xs text-text-tertiary">
        <span>
          {wordCount} word{wordCount !== 1 ? 's' : ''}
          {min_words != null && ` (min ${min_words})`}
          {max_words != null && ` / max ${max_words}`}
        </span>
        {underMin && <span className="text-warning-500">Minimum {min_words} words required</span>}
        {overLimit && <span className="text-error-500">Exceeds {max_words} word limit</span>}
      </div>
      {item.points != null && (
        <p className="text-xs text-text-tertiary">{item.points} pt{item.points !== 1 ? 's' : ''} — manually graded</p>
      )}
    </div>
  );
};

export default FreeTextEssayView;
